// Email for "Request to Host" on an Up For Grabs slot. The in-app bell
// (notifyLcOnOhRequest in helper.js) already reaches the routed LC or the
// Admin/Supervisor queue; this sends the same people a real email too
// (Henry, 2026-08-31). Pure builders live here so they can be unit-tested;
// the send goes through the shared /send-email-custom Cloud Function, which
// falls back to the team Gmail account when the sender has none connected.

import { formatTimeRange } from "./openHouseHelpers";
import { sendOpenHouseEmail } from "./emailSender";

// Same audience as the bell: the routed LC when one was resolved at post
// time, otherwise every active Admin/Supervisor. Deactivated users
// (type 1/2) and users without an email address are skipped.
export function resolveOhRequestEmailRecipients(
  opportunity,
  allUsers,
  { excludeEmails = [] } = {},
) {
  const active = (allUsers || []).filter(
    (u) => u && u.type !== 1 && u.type !== 2,
  );
  const targets = opportunity?.approverLcId
    ? active.filter((u) => u.id === opportunity.approverLcId)
    : active.filter((u) => u.role === "Admin" || u.role === "Supervisor");
  const excluded = excludeEmails
    .map((e) => String(e || "").trim().toLowerCase())
    .filter(Boolean);
  return targets.filter((u) => {
    const email = String(u.email || "").trim().toLowerCase();
    return !!email && !excluded.includes(email);
  });
}

// "2026-09-05" → "09/05/2026". String work only — running the input through
// Date() would shift it a day for some timezones.
function fmtDateMDY(iso) {
  const [y, m, d] = String(iso || "").split("-");
  if (!y || !m || !d) return String(iso || "");
  return `${m}/${d}/${y}`;
}

export function buildOhRequestEmail({ opportunity, request, origin }) {
  const shortAddr =
    opportunity?.propertyAddress?.split(",")[0] || "a listing";
  const base =
    origin ||
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    "";
  // HashRouter: route lives behind `#`, otherwise login redirect eats the path
  const boardUrl = `${base}/#/dashboard/open-houses?tab=up-for-grabs`;
  const when = `${fmtDateMDY(request?.proposedDate)}, ${formatTimeRange(
    request?.proposedStartTime,
    request?.proposedEndTime,
  )}`;
  const noteBlock =
    request?.note && request.note.trim()
      ? `\nNote: ${request.note.trim()}\n`
      : "";
  const text = `${request?.agentName || "An agent"} wants to host an open house.

Listing: ${opportunity?.propertyAddress || shortAddr}
Proposed: ${when}
${noteBlock}
Approve or decline on the Up For Grabs board:
${boardUrl}

Thanks,
Team Edelstein`;
  return { subject: `Open House Request: ${shortAddr}`, text };
}

// Fire-and-forget from RequestToHostModal. Sends as the requesting agent
// (their Gmail if connected, the team account otherwise) and never throws —
// the request itself and the bell must not depend on email delivery.
export async function emailApproversOnOhRequest({
  opportunity,
  request,
  allUsers,
  sender,
}) {
  const recipients = resolveOhRequestEmailRecipients(opportunity, allUsers, {
    excludeEmails: [sender?.email],
  });
  if (!recipients.length) return;
  const { subject, text } = buildOhRequestEmail({ opportunity, request });
  const results = await Promise.allSettled(
    recipients.map((u) =>
      sendOpenHouseEmail({
        userId: sender?.id,
        to: u.email,
        toName: `${u.firstname || ""} ${u.lastname || ""}`.trim(),
        subject,
        text,
        type: "oh-request",
        relatedDealId: String(opportunity?.dealId || ""),
      }),
    ),
  );
  results.forEach((r) => {
    if (r.status === "rejected") {
      console.error("OH request email failed:", r.reason);
    } else if (r.value && r.value.ok === false) {
      console.error("OH request email failed:", r.value.error);
    }
  });
}
